import { makeAutoObservable } from 'mobx';
import { ViewModel } from 'mobx-utils';
import { GroupApiResponse, GroupsApi } from '../../api/GroupApi';
import { Storage } from '../../utils/Storage';
import { Group } from '../models/Group';
import { Rule } from '../models/Rule';
import {
  ApportionmentAssignmentAction,
  ApportionmentType,
  RuleType,
} from '../../config/Constants';
import { Assignment } from '../models/apportionments/Assignment';
import { Apportionment } from '../models/apportionments/Apportionment';

const GROUP_LOCAL_STORAGE_KEY = 'lastGroupId';

export class GroupStore {
  groups: Map<number, Group> = new Map();
  current: number = 0;
  initialLoadCompleted: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  get currentGroup() {
    return this.groups.get(this.current);
  }

  createGroup = (newGroup: Group): Promise<Group> =>
    GroupsApi.create(newGroup.toApi()).then((group: GroupApiResponse) => {
      const groupObj = Group.fromApi(group);
      this.groups.set(group.id, groupObj);
      this.setCurrentGroup(group.id);
      return groupObj;
    });

  loadGroup = (groupId: number): Promise<Group> =>
    GroupsApi.getGroup(groupId).then((group: GroupApiResponse) => {
      if (this.groups.has(groupId)) {
        this.groups.get(groupId).loadFromApi(group);
        return this.groups.get(groupId);
      }

      const groupObj = Group.fromApi(group);
      this.groups.set(group.id, groupObj);
      return groupObj;
    });

  getGroup = (groupId: number): Group => {
    return this.groups.get(groupId);
  };

  setGroup = (group: Group): void => {
    this.groups.set(group.id, group);
  };

  /**
   * Gets whatever group is referred by local storage.
   * If that group isn't accessible, take one of the groups that is accessible by user and refresh local storage.
   * Return undefined otherwise.
   */
  defaultGroup = (): Group => {
    const storedId = parseInt(Storage.get(GROUP_LOCAL_STORAGE_KEY));
    if (storedId && this.groups.has(storedId)) {
      return this.groups.get(storedId);
    }
    const existingGroup = this.getAnyGroup();
    if (existingGroup) {
      Storage.set(GROUP_LOCAL_STORAGE_KEY, `${existingGroup.id}`);
    } else {
      Storage.delete(GROUP_LOCAL_STORAGE_KEY);
    }
    return existingGroup;
  };

  /**
   * Return any group, giving preference to onboarded groups
   */
  getAnyGroup() {
    const allGroups = Array.from(this.groups.values());
    return allGroups.find(group => group.hasOnboarded) ?? allGroups[0];
  }

  setCurrentGroup = (groupId: number): void => {
    this.current = groupId;
    Storage.set(GROUP_LOCAL_STORAGE_KEY, `${groupId}`);
  };

  hasBrokenRules = (): boolean => {
    return (
      this.currentGroup &&
      this.currentGroup.entities &&
      this.currentGroup.entities
        .map(entity => entity.rules)
        .reduce((a, b) => a.concat(b), [])
        .filter(rule => this.ruleHasBrokenApportionments(rule)).length > 0
    );
  };

  ruleHasBrokenApportionments = (rule: Rule): boolean => {
    if (
      rule.apportionment?.id === 0 ||
      rule.apportionment instanceof ViewModel ||
      rule.type === RuleType.NONE ||
      rule.type === RuleType.NONE_SALES ||
      rule.type === RuleType.NONE_BALANCE
    ) {
      return false;
    }

    if (!rule.apportionment) {
      return true;
    }

    const noEntities = rule.rechargeEntities.length === 0;

    if (noEntities) {
      return noEntities;
    }

    const nomCodeAssignments = new Map<
      number,
      {
        entityId: number;
        nomCode: string;
        setting: ApportionmentAssignmentAction;
      }
    >();

    rule.apportionment.splits.forEach(split => {
      if (split.split === 0) return;

      if (split.subApportionment.type === ApportionmentType.ASSIGNMENT) {
        const id = (split.subApportionment as Assignment).nominalCode
          ?.externalId;

        if (
          !(
            rule.apportionment.nominalCodeAction ===
              ApportionmentAssignmentAction.RETAIN &&
            split.entityId === rule.creatingEntity
          )
        ) {
          nomCodeAssignments.set(split.siblingId, {
            entityId: split.entityId,
            nomCode: id,
            setting: rule.apportionment.nominalCodeAction,
          });
        }
      } else {
        const subAppt = split.subApportionment as Apportionment;
        subAppt.splits.forEach(subSplit => {
          const id = (subSplit.subApportionment as Assignment).nominalCode
            ?.externalId;

          if (
            !(
              subAppt.nominalCodeAction ===
                ApportionmentAssignmentAction.RETAIN &&
              split.entityId === rule.creatingEntity
            )
          ) {
            nomCodeAssignments.set(subSplit.siblingId, {
              entityId: split.entityId,
              nomCode: id,
              setting: subAppt.nominalCodeAction,
            });
          }
        });
      }
    });

    rule.assignmentSettings.forEach(asgnSetting => {
      if (nomCodeAssignments.has(asgnSetting.id)) {
        if (asgnSetting.ignore) {
          nomCodeAssignments.delete(asgnSetting.id);
        } else if (
          nomCodeAssignments.get(asgnSetting.id).setting !==
          ApportionmentAssignmentAction.SET
        ) {
          nomCodeAssignments.get(asgnSetting.id).nomCode =
            asgnSetting.ncExternalId;
        }
      }
    });

    const missingNomCodes = new Map(
      [...nomCodeAssignments.entries()].filter(e => {
        const entity = this.currentGroup.getEntity(e[1].entityId);
        return (
          !e[1].nomCode ||
          e[1].nomCode === '' ||
          (entity && !entity.getNominalCodeByExternalId(e[1].nomCode))
        );
      })
    );

    return missingNomCodes.size > 0;
  };
}
